/* eslint-disable no-mixed-operators */
/* eslint-disable i18next/no-literal-string */
import React, { useState, useCallback, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { ReactComponent as Title } from 'assets/images/general-landing/Title.svg';
import { Button } from 'react-bootstrap';
import hero1Image from 'assets/images/general-landing/hero-1.png';
import hero2Image from 'assets/images/general-landing/hero-2.png';
import hero3Image from 'assets/images/general-landing/hero-3.png';
import hero4Image from 'assets/images/general-landing/hero-4.png';
import { useHistory } from 'react-router-dom';
import LandingHeader from 'components/Common/LandingHeaderV2';
import styles from './Hero.module.scss';

export default function Hero() {
  const history = useHistory();
  // eslint-disable-next-line no-unused-vars
  const [loadedImages, setLoadedImages] = useState({
    hero1: false,
    hero2: false,
    hero3: false,
    hero4: false,
  });

  const [animationStep, setAnimationStep] = useState(0); // 0: loading/waiting, 1: fly to center, 2: move to final positions, 3: scroll effects
  const [scrollY, setScrollY] = useState(0);
  const [isScrollMode, setIsScrollMode] = useState(false);

  const screenWidth = window.innerWidth;
  const img1Width = screenWidth * 0.6;
  const img1Height = img1Width * 525 / 932;

  // Scroll event handler
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      // Switch to scroll mode after step 2 is complete and user starts scrolling
      if (animationStep === 2 && currentScrollY > 100 && !isScrollMode) {
        setIsScrollMode(true);
        setAnimationStep(3);
        console.log('🎯 Step 3: Scroll effects activated');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [animationStep, isScrollMode]);

  // Calculate scroll-based transformations
  const getScrollTransform = (isMainImage = false) => {
    if (!isScrollMode) return { scale: 1, spread: 0, blur: 0 };

    const scrollProgress = Math.min(scrollY / 800, 1); // Normalize scroll to 0-1 over 800px

    if (isMainImage) {
      // Main image (hero1): zoom out effect only
      const scale = 1 + (scrollProgress * 0.3); // Zoom out effect
      return { scale, spread: 0, blur: 0 };
    }
    // Supporting images (hero2, 3, 4): minimize and blur
    const scale = 1 - (scrollProgress * 0.4); // Minimize effect (shrink down)
    const spread = scrollProgress * 100; // Split out effect
    const blur = scrollProgress * 8; // Blur effect (0 to 8px)
    return { scale: Math.max(scale, 0.3), spread, blur }; // Minimum scale of 0.3
  };

  // Helper functions to get animation targets
  const getHero1Target = () => {
    const { scale } = getScrollTransform(true); // Main image

    if (animationStep === 0) {
      return { top: 1000, left: '50%', opacity: 0, transform: 'translateX(-50%) scale(1)', filter: 'blur(0px)' }; // Step 0: Loading - images hidden below
    }
    if (animationStep === 1) {
      return { top: -400, left: '50%', opacity: 1, transform: 'translateX(-50%) scale(0.6)', filter: 'blur(0px)' }; // Step 1: Fly to signup button position (smaller)
    }
    if (animationStep === 2) {
      return { top: 0, left: '50%', opacity: 1, transform: 'translateX(-50%) scale(1)', filter: 'blur(0px)' }; // Step 2: Move to final position (original size)
    }
    if (animationStep === 3) {
      // Step 3: Scroll effects - zoom out only (no blur for main image)
      const followScroll = scrollY > 600 ? Math.min(scrollY - 600, 200) : 0;
      return {
        top: 0 + followScroll,
        left: '50%',
        opacity: 1,
        transform: `translateX(-50%) scale(${scale})`,
        filter: 'blur(0px)',
      };
    }
    return { top: 1000, left: '50%', opacity: 0, transform: 'translateX(-50%) scale(1)', filter: 'blur(0px)' }; // Default state
  };

  const getHero2Target = () => {
    const { scale, spread, blur } = getScrollTransform(false); // Supporting image

    if (animationStep === 0) {
      return { top: 1000, left: 0, opacity: 0, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 0: Loading - images hidden below
    }
    if (animationStep === 1) {
      return { top: -350, left: 200 * img1Width / 932, opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 1: Fly to signup button area (original size)
    }
    if (animationStep === 2) {
      return { top: -139, left: -320 * img1Width / 932, opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 2: Move to final position (original size)
    }
    if (animationStep === 3) {
      // Step 3: Split out, minimize and blur effect
      const followScroll = scrollY > 600 ? Math.min(scrollY - 600, 200) : 0;
      return {
        top: -139 + followScroll,
        left: (-320 - spread) * img1Width / 932,
        opacity: 1,
        transform: `scale(${scale})`,
        filter: `blur(${blur}px)`,
      };
    }
    return { top: 1000, left: 0, opacity: 0, transform: 'scale(1)', filter: 'blur(0px)' }; // Default state
  };

  const getHero3Target = () => {
    const { scale, spread, blur } = getScrollTransform(false); // Supporting image

    if (animationStep === 0) {
      return { bottom: -1000, left: 0, opacity: 0, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 0: Loading - images hidden below
    }
    if (animationStep === 1) {
      return { bottom: 400, left: 200 * img1Width / 932, opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 1: Fly to signup button area (original size)
    }
    if (animationStep === 2) {
      return { bottom: 30, left: -232 * img1Width / 932, opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 2: Move to final position (original size)
    }
    if (animationStep === 3) {
      // Step 3: Split out, minimize and blur effect
      const followScroll = scrollY > 600 ? Math.min(scrollY - 600, 200) : 0;
      return {
        bottom: 30 - followScroll,
        left: (-232 - spread) * img1Width / 932,
        opacity: 1,
        transform: `scale(${scale})`,
        filter: `blur(${blur}px)`,
      };
    }
    return { bottom: -1000, left: 0, opacity: 0, transform: 'scale(1)', filter: 'blur(0px)' }; // Default state
  };

  const getHero4Target = () => {
    const { scale, spread, blur } = getScrollTransform(false); // Supporting image

    if (animationStep === 0) {
      return { top: 1000, right: 0, opacity: 0, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 0: Loading - images hidden below
    }
    if (animationStep === 1) {
      return { top: -300, right: 200 * img1Width / 932, opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 1: Fly to signup button area (original size)
    }
    if (animationStep === 2) {
      return { top: -100, right: -299 * img1Width / 932, opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }; // Step 2: Move to final position (original size)
    }
    if (animationStep === 3) {
      // Step 3: Split out, minimize and blur effect
      const followScroll = scrollY > 600 ? Math.min(scrollY - 600, 200) : 0;
      return {
        top: -100 + followScroll,
        right: (-299 - spread) * img1Width / 932,
        opacity: 1,
        transform: `scale(${scale})`,
        filter: `blur(${blur}px)`,
      };
    }
    return { top: 1000, right: 0, opacity: 0, transform: 'scale(1)', filter: 'blur(0px)' }; // Default state
  };

  // Helper functions for delays
  const getHero1Delay = () => {
    if (animationStep === 0) return 0; // No delay for loading state
    if (animationStep === 1) return 800; // Image 1 comes last to center
    if (animationStep === 2) return 200; // First to final
    return 0;
  };

  const getHero2Delay = () => {
    if (animationStep === 0) return 0; // No delay for loading state
    if (animationStep === 1) return 0; // Image 2 starts first to center
    if (animationStep === 2) return 0; // Then to final
    return 0;
  };

  const getHero3Delay = () => {
    if (animationStep === 0) return 0; // No delay for loading state
    if (animationStep === 1) return 300; // 300ms after image 2 to center
    if (animationStep === 2) return 100;
    return 0;
  };

  const getHero4Delay = () => {
    if (animationStep === 0) return 0; // No delay for loading state
    if (animationStep === 1) return 500; // 500ms after image 2 to center
    if (animationStep === 2) return 150;
    return 0;
  };

  // Animation springs for UI elements (header, title, subtitle, button)
  const uiElementsSpring = useSpring({
    from: {
      opacity: 0,
      transform: 'translateY(20px)', // Start slightly below with fade
    },
    to: animationStep >= 2 ? {
      opacity: 1,
      transform: 'translateY(0px)', // Slide up and fade in
    } : {
      opacity: 0,
      transform: 'translateY(20px)', // Hidden and below
    },
    config: { tension: 120, friction: 20 },
    delay: animationStep === 2 ? 400 : 0, // Appear 400ms after final step starts
    onStart: () => {
      if (animationStep === 2) {
        console.log('✨ Step 2 UI: Header, title, subtitle, and button appearing');
      }
    },
    onRest: () => {
      if (animationStep === 2) {
        console.log('🎊 Animation Complete: All elements are now in their final positions!');
      }
    },
  });

  // Animation springs for each image
  const hero1Spring = useSpring({
    from: {
      top: 1000, // Start far below viewport
      left: '50%',
      opacity: 0,
    },
    to: getHero1Target(),
    config: { tension: 120, friction: 14 },
    delay: getHero1Delay(),
    onRest: () => {
      // Trigger step 2 when step 1 completes for hero1 (last image)
      if (animationStep === 1) {
        setTimeout(() => setAnimationStep(2), 500); // Wait 500ms before step 2
      }
    },
  });

  const hero2Spring = useSpring({
    from: {
      top: 1000, // Start far below viewport
      left: 0,
      opacity: 0,
    },
    to: getHero2Target(),
    config: { tension: 120, friction: 14 },
    delay: getHero2Delay(),
  });

  const hero3Spring = useSpring({
    from: {
      bottom: -1000, // Start far below viewport
      left: 0,
      opacity: 0,
    },
    to: getHero3Target(),
    config: { tension: 120, friction: 14 },
    delay: getHero3Delay(),
  });

  const hero4Spring = useSpring({
    from: {
      top: 1000, // Start far below viewport
      right: 0,
      opacity: 0,
    },
    to: getHero4Target(),
    config: { tension: 120, friction: 14 },
    delay: getHero4Delay(),
  });

  const onAllImagesLoaded = useCallback(() => {
    console.log('🎉 All 4 hero images have successfully loaded!');
    console.log('📋 Step 0 Complete: All images loaded');
    // Wait a moment to show loading is complete, then start step 1
    setTimeout(() => {
      console.log('🚀 Starting Step 1: Flying to center');
      setAnimationStep(1);
    }, 300); // 300ms delay to show loading completion
  }, []);

  const handleImageLoad = useCallback((imageName) => {
    setLoadedImages(prev => {
      const newState = { ...prev, [imageName]: true };

      // Check if all images are loaded
      const allLoaded = Object.values(newState).every(loaded => loaded);

      if (allLoaded) {
        // Callback when all images are loaded
        console.log('All images have loaded!');
        onAllImagesLoaded();
      }

      return newState;
    });
  }, [onAllImagesLoaded]);

  return (
    <div className={styles.container}>
      <animated.div style={{
        ...uiElementsSpring,
        zIndex: 1,
        position: 'relative',
      }}
      >
        <LandingHeader />
      </animated.div>
      <div className={styles.hero}>
        <animated.div className={styles.title} style={uiElementsSpring}>
          <Title />
        </animated.div>
        <animated.div className={styles.subtitle} style={uiElementsSpring}>
          Transform Your Listings with DIY Betates
        </animated.div>
        <animated.div style={uiElementsSpring}>
          <Button
            variant="primary"
            onClick={() => {
              history.push('/get-started');
            }}
          >
            Free sign up now
          </Button>
        </animated.div>

        <div className={styles.images}>
          <div style={{
            left: '50%',
            transform: 'translateX(-50%)',
            position: 'absolute',
            width: img1Width,
            height: img1Height,
            top: 141,
          }}
          >
            <animated.img
              src={hero1Image}
              alt="hero1"
              onLoad={() => handleImageLoad('hero1')}
              style={{
                width: img1Width,
                position: 'absolute',
                zIndex: 4, // Image 1 on top
                ...hero1Spring,
              }}
            />
            <animated.img
              src={hero2Image}
              alt="hero2"
              onLoad={() => handleImageLoad('hero2')}
              style={{
                width: 408 * img1Width / 932,
                position: 'absolute',
                zIndex: 1,
                ...hero2Spring,
              }}
            />

            <animated.img
              src={hero3Image}
              alt="hero3"
              onLoad={() => handleImageLoad('hero3')}
              style={{
                width: 208 * img1Width / 932,
                position: 'absolute',
                zIndex: 2,
                ...hero3Spring,
              }}
            />

            <animated.img
              src={hero4Image}
              alt="hero4"
              onLoad={() => handleImageLoad('hero4')}
              style={{
                width: 275 * img1Width / 932,
                position: 'absolute',
                zIndex: 3,
                ...hero4Spring,
              }}
            />
          </div>

        </div>
      </div>

      {/* Additional content to enable scrolling and demonstrate effects */}
      <div className={styles.space} />
    </div>
  );
}
